// src/features/settings/ChangeEmailDialog.tsx
import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, CircularProgress,
    Alert, Stack, Typography, Link
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import * as authService from '../../api/authService';
import { fetchUserInfo } from '../auth/authSlice';
import { useTranslation, Trans } from 'react-i18next';

interface ChangeEmailDialogProps {
    open: boolean;
    onClose: () => void;
    currentEmail: string;
}

const RESEND_TIMER_SECONDS = parseInt(process.env.REACT_APP_RESEND_CODE_TIMER || '60', 10);

const ChangeEmailDialog: React.FC<ChangeEmailDialogProps> = ({ open, onClose, currentEmail }) => {
    const dispatch = useAppDispatch();
    const { user, status } = useAppSelector(state => state.auth);
    const { t } = useTranslation();
    const [activeStep, setActiveStep] = useState(0); // 0 for email input, 1 for code verification
    const [newEmail, setNewEmail] = useState('');
    const [emailTouched, setEmailTouched] = useState(false);
    const [verificationCode, setVerificationCode] = useState(new Array(6).fill(''));
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [countdown, setCountdown] = useState(0);

    const timerRef = useRef<NodeJS.Timeout>(null);
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    // Check if email is valid and not empty
    const isEmailValid = newEmail && newEmail !== currentEmail && /\S+@\S+\.\S+/.test(newEmail);
    const showEmailError = emailTouched && !newEmail;

    useEffect(() => {
        if (!open) {
            // Reset state on close
            setActiveStep(0);
            setNewEmail('');
            setEmailTouched(false);
            setVerificationCode(new Array(6).fill(''));
            setError(null);
            setIsLoading(false);
            setCountdown(0);
            if (timerRef.current) clearInterval(timerRef.current);
        }
    }, [open]);

    useEffect(() => {
        if (countdown > 0) {
            timerRef.current = setTimeout(() => setCountdown(countdown - 1), 1000);
        }
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [countdown]);

    const handleRequestCode = async () => {
        setEmailTouched(true);
        if (!newEmail || newEmail === currentEmail) {
            setError(t('invalid_email_or_same'));
            return;
        }
        if (!isEmailValid) {
            setError(t('invalid_email_format'));
            return;
        }
        setError(null);
        setIsLoading(true);
        try {
            await authService.requestEmailChange({ newEmail });
            setIsLoading(false);
            setActiveStep(1);
            setCountdown(RESEND_TIMER_SECONDS);
        } catch (err: any) {
            setError(err.message || t('verification_failed'));
            setIsLoading(false);
        }
    };

    const handleResendCode = async () => {
        if (countdown > 0) return;
        setError(null);
        setIsLoading(true);
        try {
            await authService.requestEmailChange({ newEmail });
            setCountdown(RESEND_TIMER_SECONDS);
        } catch (err: any) {
            setError(err.message || t('verification_failed'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleConfirmChange = async () => {
        const finalCode = verificationCode.join('');
        if (finalCode.length !== 6) {
            setError(t('invalid_verification_code'));
            return;
        }
        setError(null);
        setIsLoading(true);
        try {
            await authService.confirmEmailChange({ newEmail, verificationCode: finalCode });
            // On success, refresh user info to get the new email and close dialog
            await dispatch(fetchUserInfo());
            onClose();
        } catch (err: any) {
            setError(err.message || t('verification_failed'));
            setIsLoading(false);
        }
    };

    const handleCodeChange = (element: HTMLInputElement, index: number) => {
        if (isNaN(Number(element.value))) return;

        const newCode = [...verificationCode];
        newCode[index] = element.value;
        setVerificationCode(newCode);

        if (element.value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, index: number) => {
        if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>{t('change_email_title')}</DialogTitle>
            <DialogContent>
                {/* Instruction text below the title */}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {user?.isGoogleLinked
                        ? <Trans i18nKey="google_linked_instruction" components={{ strong: <strong /> }} />
                        : t('change_email_instruction')}
                </Typography>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {activeStep === 0 && !user?.isGoogleLinked && (
                    <Box sx={{ mt: 1 }}>
                        <TextField
                            autoFocus
                            margin="dense"
                            id="newEmail"
                            label={t('new_email')}
                            type="email"
                            fullWidth
                            variant="outlined"
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                            onBlur={() => setEmailTouched(true)}
                            disabled={isLoading}
                            error={showEmailError}
                            helperText={showEmailError ? t('email_required') : ''}
                        />
                    </Box>
                )}

                {activeStep === 1 && (
                    <Box sx={{ mt: 1, width: '100%' }}>
                        <Typography variant="body2" align="center" color="text.secondary">
                            <Trans i18nKey="email_verification_sent" values={{ email: newEmail }} components={{ strong: <strong /> }} />
                        </Typography>
                        <Stack direction="row" spacing={1} justifyContent="center" sx={{ my: 3 }}>
                            {verificationCode.map((data, index) => (
                                <TextField
                                    key={index}
                                    sx={{ width: '40px' }}
                                    inputRef={el => inputRefs.current[index] = el}
                                    value={data}
                                    onChange={e => handleCodeChange(e.target as HTMLInputElement, index)}
                                    onKeyDown={e => handleKeyDown(e, index)}
                                    inputProps={{
                                        maxLength: 1,
                                        style: { textAlign: 'center', fontSize: '1.2rem' }
                                    }}
                                />
                            ))}
                        </Stack>
                        <Box textAlign="center" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                            <Link component="button" variant="body2" onClick={() => setActiveStep(0)} color="inherit" type="button">
                                {t('change_email')}
                            </Link>
                            <Button
                                onClick={handleResendCode}
                                disabled={countdown > 0 || isLoading}
                                size="small"
                            >
                                {countdown > 0 ? t('resend_in_seconds', { countdown }) : t('resend_code')}
                            </Button>
                        </Box>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isLoading}>{t('cancel')}</Button>
                {activeStep === 0 && !user?.isGoogleLinked && (
                    <Button
                        onClick={handleRequestCode}
                        variant="contained"
                        disabled={isLoading || !isEmailValid}
                        sx={{
                            bgcolor: 'inverse.main',
                            color: 'inverse.contrastText',
                            '&:hover': {
                                bgcolor: 'inverse.hover.main',
                            },
                        }}
                    >
                        {isLoading ? <CircularProgress size={24} /> : t('send_code')}
                    </Button>
                )}
                {activeStep === 1 && (
                    <Button
                        onClick={handleConfirmChange}
                        variant="contained"
                        disabled={isLoading}
                        sx={{
                            bgcolor: 'inverse.main',
                            color: 'inverse.contrastText',
                            '&:hover': {
                                bgcolor: 'inverse.hover.main',
                            },
                        }}
                    >
                        {isLoading ? <CircularProgress size={24} /> : t('verify_and_change')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ChangeEmailDialog;